import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Search, MapPin, ChevronDown, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * 2-field marketplace search bar.
 *
 * Per product spec (2026-06-21):
 *   • Field 1 — "Service / Package / Product / Salon" free text. Posts to
 *     /m/search?q=... and the backend matches against service names,
 *     package names, product names, and listing name/tags/about blobs.
 *   • Field 2 — Area (searchable dropdown). Source: the `/marketplace/areas-with-venues`
 *     endpoint, which returns ONLY areas that currently host at least one
 *     active marketplace listing branch. Empty result → silent fallback to
 *     the full Qatar area list.
 *   • Date is REMOVED — a customer's preferred date is asked later, on the
 *     venue page slot picker.
 */
export default function MpSearchBar({ variant = "hero", defaultQ = "", defaultArea = "" }) {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isAr = lang === "ar";
  const [q, setQ] = useState(defaultQ);
  const [areaId, setAreaId] = useState(defaultArea);
  const [areas, setAreas] = useState([]);
  const isCompact = variant === "compact";

  // Load areas with venues once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/marketplace/areas-with-venues");
        if (alive) setAreas(r.data?.items || []);
      } catch {
        if (alive) setAreas([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (areaId) params.set("area", areaId);
    navigate(`/m/search?${params.toString()}`);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="mp-search-bar"
      className={`bg-white rounded-2xl shadow-xl flex flex-col md:flex-row items-stretch border border-[var(--mp-border)] ${isCompact ? "" : "p-1"} relative`}
    >
      {/* === Q field === */}
      <div className={`flex-1 flex items-center gap-3 px-4 ${isCompact ? "py-2.5" : "py-3"} border-b md:border-b-0 md:border-e border-[var(--mp-divider)]`}>
        <Search size={18} className="text-[var(--mp-maroon-dark)] shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={isAr ? "اسم خدمة، باقة، منتج، أو صالون" : "Service, package, product, or salon"}
          data-testid="mp-search-q"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--mp-ink-soft)]"
        />
        {q && (
          <button type="button" onClick={() => setQ("")} data-testid="mp-search-q-clear" className="opacity-50 hover:opacity-100 transition">
            <X size={14} />
          </button>
        )}
      </div>

      {/* === Area searchable dropdown === */}
      <AreaDropdown
        value={areaId}
        onChange={setAreaId}
        areas={areas}
        isAr={isAr}
        isCompact={isCompact}
      />

      <button type="submit" data-testid="mp-search-submit" className={`mp-btn mp-btn-primary ${isCompact ? "!rounded-xl m-1 !px-5" : "!rounded-xl m-1.5 !px-7 !py-3.5"} shrink-0`}>
        {isAr ? "بحث" : "Search"}
      </button>
    </form>
  );
}

// ============================================================================
// AreaDropdown — searchable select. Filters `areas` against the typed string
// (matches against EN + AR names + municipality). Closes on outside click.
// ============================================================================
function AreaDropdown({ value, onChange, areas, isAr, isCompact }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selected = useMemo(() => areas.find((a) => a.area_id === value), [areas, value]);

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase().trim();
    const fq = norm(filter);
    if (!fq) return areas;
    return areas.filter((a) =>
      norm(a.name?.en).includes(fq) ||
      norm(a.name?.ar).includes(fq) ||
      norm(a.municipality).includes(fq) ||
      norm(a.municipality_name?.en).includes(fq) ||
      norm(a.municipality_name?.ar).includes(fq)
    );
  }, [areas, filter]);

  const label = selected
    ? (isAr ? (selected.name?.ar || selected.name?.en) : (selected.name?.en || selected.name?.ar))
    : (isAr ? "اختر المنطقة" : "Select area");

  return (
    <div ref={wrapRef} className={`flex-1 relative ${isCompact ? "py-2.5" : "py-3"} px-4 border-b md:border-b-0 md:border-e border-[var(--mp-divider)]`} data-testid="mp-search-area-wrap">
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        data-testid="mp-search-area-toggle"
        className="w-full flex items-center gap-3 text-start"
      >
        <MapPin size={18} className="text-[var(--mp-maroon-dark)] shrink-0" />
        <span className={`flex-1 text-sm truncate ${selected ? "text-[var(--mp-ink)]" : "text-[var(--mp-ink-soft)]"}`}>{label}</span>
        {selected && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(""); setFilter(""); }}
            data-testid="mp-search-area-clear"
            className="opacity-50 hover:opacity-100 transition"
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown size={15} className={`opacity-60 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute start-0 end-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-[var(--mp-border)] z-50 max-h-[340px] flex flex-col"
          data-testid="mp-search-area-dropdown"
        >
          <div className="p-2 border-b border-[var(--mp-divider)] sticky top-0 bg-white">
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={isAr ? "ابحث عن منطقة..." : "Search areas..."}
              data-testid="mp-search-area-filter"
              className="w-full px-3 py-2 rounded-md border border-[var(--mp-border)] bg-white text-sm outline-none focus:border-[var(--mp-maroon-dark)]"
            />
          </div>
          <ul className="overflow-y-auto flex-1" data-testid="mp-search-area-list">
            {filtered.length === 0 ? (
              <li className="px-4 py-6 text-sm text-[var(--mp-ink-muted)] text-center">
                {isAr ? "لا توجد مناطق مطابقة" : "No matching areas"}
              </li>
            ) : (
              filtered.map((a) => {
                const muniName = isAr ? (a.municipality_name?.ar || a.municipality_name?.en) : (a.municipality_name?.en || a.municipality_name?.ar);
                const areaName = isAr ? (a.name?.ar || a.name?.en) : (a.name?.en || a.name?.ar);
                return (
                  <li key={a.area_id}>
                    <button
                      type="button"
                      onClick={() => { onChange(a.area_id); setOpen(false); setFilter(""); }}
                      data-testid={`mp-search-area-option-${a.area_id}`}
                      className={`w-full text-start px-4 py-2.5 hover:bg-[var(--mp-cream)] text-sm flex items-center justify-between gap-3 ${value === a.area_id ? "bg-[var(--mp-cream)] font-bold" : ""}`}
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="truncate">{areaName}</span>
                        {muniName && <span className="text-[11px] text-[var(--mp-ink-muted)] truncate">{muniName}</span>}
                      </span>
                      {a.venue_count != null && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 shrink-0">
                          {a.venue_count} {isAr ? "صالون" : a.venue_count === 1 ? "salon" : "salons"}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
